import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDataProvider } from '@/providers/DataProviderContext'
import { Book as BookType, BookTemplate, BOOK_TEMPLATES, CoverDesign } from '@/core/models'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Input,
  Textarea,
  AppHeader,
  AppFooter,
  TemplateSelector,
  CoverDesigner,
} from '@/components'
import { Book, Plus, Edit, Trash2, ArrowRight, ArrowLeft, Palette } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

export function BooksDashboard() {
  const navigate = useNavigate()
  const dataProvider = useDataProvider()

  const [books, setBooks] = useState<BookType[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [modalStep, setModalStep] = useState<'template' | 'details'>('template')
  const [selectedTemplate, setSelectedTemplate] = useState<BookTemplate>(BOOK_TEMPLATES[0])

  // Cover designer state
  const [isCoverDesignerOpen, setIsCoverDesignerOpen] = useState(false)
  const [selectedBookForCover, setSelectedBookForCover] = useState<BookType | null>(null)

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [author, setAuthor] = useState('')

  useEffect(() => {
    loadBooks()
  }, [])

  const loadBooks = async () => {
    setLoading(true)
    try {
      const fetchedBooks = await dataProvider.getBooks()
      setBooks(fetchedBooks)
    } catch (error) {
      console.error('Failed to load books:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateBook = async () => {
    if (!title.trim()) return

    try {
      // Create the book first
      const newBook = await dataProvider.createBook({
        title: title.trim(),
        description: description.trim(),
        author: author.trim() || 'Anonymous',
        genre: selectedTemplate.genre || ['General'],
      })

      // Create template chapters if any
      if (selectedTemplate.chapters && selectedTemplate.chapters.length > 0) {
        for (const chapterTemplate of selectedTemplate.chapters) {
          await dataProvider.createChapter({
            bookId: newBook.id,
            title: chapterTemplate.title,
            content: chapterTemplate.content,
            order: chapterTemplate.order,
          })
        }
      }

      // Reset form
      setTitle('')
      setDescription('')
      setAuthor('')
      setModalStep('template')
      setSelectedTemplate(BOOK_TEMPLATES[0])
      setIsCreateModalOpen(false)

      // Reload books and navigate to editor
      await loadBooks()

      // Open the new book in editor
      navigate(`/editor/${newBook.id}`)
    } catch (error) {
      console.error('Failed to create book:', error)
    }
  }

  const handleDeleteBook = async (bookId: string) => {
    if (!confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      return
    }

    try {
      await dataProvider.deleteBook(bookId)
      await loadBooks()
    } catch (error) {
      console.error('Failed to delete book:', error)
    }
  }

  const handleOpenBook = (bookId: string) => {
    navigate(`/editor/${bookId}`)
  }

  const handleOpenCoverDesigner = (book: BookType) => {
    setSelectedBookForCover(book)
    setIsCoverDesignerOpen(true)
  }

  const handleSaveCoverDesign = async (
    design: Omit<CoverDesign, 'id' | 'bookId' | 'createdAt' | 'updatedAt'>,
  ) => {
    if (!selectedBookForCover) return

    try {
      await dataProvider.updateBook(selectedBookForCover.id, {
        coverDesign: design,
      })

      setIsCoverDesignerOpen(false)
      setSelectedBookForCover(null)
      await loadBooks()
    } catch (error) {
      console.error('Failed to save cover design:', error)
      alert('Failed to save cover design')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Professional Header */}
      <AppHeader onNewBook={() => setIsCreateModalOpen(true)} />

      {/* Main Content */}
      <main className="flex-1 p-3 sm:p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
        {/* Page Info */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">My Books</h2>
          <p className="text-sm sm:text-base text-gray-600">
            {books.length === 0
              ? 'Get started by creating your first ebook'
              : books.length === 1 && books[0].chapterIds?.length === 0
              ? 'Click on your book to start writing chapters'
              : `${books.length} ${books.length === 1 ? 'book' : 'books'} in your library`}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Spinner size="xl" />
          </div>
        )}

        {/* Empty State */}
        {!loading && books.length === 0 && (
          <Card>
            <CardContent className="py-12 sm:py-16 md:py-20 px-4">
              <div className="text-center">
                <Book className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 sm:mb-2">
                  No books yet
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-md mx-auto">
                  Start writing your first ebook by clicking the button below
                </p>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Create Your First Book
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Books Grid */}
        {!loading && books.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {books.map(book => (
              <Card key={book.id} hover onClick={() => handleOpenBook(book.id)} className="cursor-pointer">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="line-clamp-2 text-base sm:text-lg">{book.title}</CardTitle>
                      <CardDescription className="mt-0.5 sm:mt-1 text-xs sm:text-sm">
                        by {book.author}
                      </CardDescription>
                    </div>
                    <Badge variant={book.status === 'published' ? 'success' : 'default'} className="text-xs shrink-0">
                      {book.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 pb-3">
                  {book.description && (
                    <p className="text-gray-700 text-xs sm:text-sm line-clamp-2 sm:line-clamp-3 mb-3 sm:mb-4">
                      {book.description}
                    </p>
                  )}
                  <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center justify-between gap-2">
                      <span className="shrink-0">Genre:</span>
                      <span className="font-medium truncate">{book.metadata.genre.join(', ') || 'General'}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="shrink-0">Chapters:</span>
                      <span className="font-medium">{book.chapterIds?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="shrink-0">Updated:</span>
                      <span className="font-medium truncate text-xs sm:text-sm">
                        {formatDistanceToNow(new Date(book.updatedAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-3">
                  <div className="flex gap-2 w-full">
                    <Button
                      variant="primary"
                      size="sm"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenBook(book.id)
                      }}
                      className="text-xs sm:text-sm"
                    >
                      <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                      <span className="hidden xs:inline">Edit</span>
                      <span className="xs:hidden">Open</span>
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenCoverDesigner(book)
                      }}
                      className="px-2 sm:px-3"
                      title="Design Cover"
                    >
                      <Palette className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteBook(book.id)
                      }}
                      className="px-2 sm:px-3"
                    >
                      <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
        </div>
      </main>

      {/* Create Book Modal */}
      <Modal
        open={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false)
          setModalStep('template')
        }}
        size="lg"
      >
        <ModalHeader>
          <ModalTitle>
            {modalStep === 'template' ? 'Create New Book' : 'Book Details'}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {modalStep === 'template' ? (
            <TemplateSelector
              onSelect={setSelectedTemplate}
              selectedTemplateId={selectedTemplate.id}
            />
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-gray-900">
                  Template: {selectedTemplate.icon} {selectedTemplate.name}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {selectedTemplate.description}
                </p>
              </div>

              <Input
                label="Title"
                placeholder="Enter your book title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                fullWidth
                autoFocus
              />
              <Input
                label="Author"
                placeholder="Your name"
                value={author}
                onChange={e => setAuthor(e.target.value)}
                fullWidth
              />
              <Textarea
                label="Description (optional)"
                placeholder="What is your book about?"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={4}
                fullWidth
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {modalStep === 'template' ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setIsCreateModalOpen(false)
                  setModalStep('template')
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setModalStep('details')}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setModalStep('template')}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateBook}
                disabled={!title.trim()}
              >
                Create Book
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {/* Cover Designer Modal */}
      <Modal
        open={isCoverDesignerOpen}
        onClose={() => {
          setIsCoverDesignerOpen(false)
          setSelectedBookForCover(null)
        }}
        size="xl"
      >
        <ModalHeader>
          <ModalTitle>Design Book Cover</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {selectedBookForCover && (
            <CoverDesigner
              bookTitle={selectedBookForCover.title}
              bookAuthor={selectedBookForCover.author}
              initialDesign={selectedBookForCover.coverDesign}
              onSave={handleSaveCoverDesign}
            />
          )}
        </ModalBody>
      </Modal>

      {/* Professional Footer */}
      <AppFooter />
    </div>
  )
}
